import { useState, useRef, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";

export default function BeforeAfterSlider({ before, after, className = "" }) {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  const onMove = useCallback((e) => {
    if (!dragging.current) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromClientX(clientX);
  }, [updateFromClientX]);

  const stop = useCallback(() => {
    dragging.current = false;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", stop);
    window.removeEventListener("touchmove", onMove);
    window.removeEventListener("touchend", stop);
  }, [onMove]);

  const start = useCallback(() => {
    dragging.current = true;
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", stop);
  }, [onMove, stop]);

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden select-none cursor-ew-resize aspect-[4/3] bg-muted ${className}`}
      onClick={(e) => updateFromClientX(e.clientX)}
      data-testid="before-after-slider"
    >
      <img src={after} alt="After" className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} />
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
        <img src={before} alt="Before" className="absolute inset-0 h-full object-cover" style={{ width: containerRef.current ? containerRef.current.offsetWidth : "100%", maxWidth: "none" }} draggable={false} />
      </div>

      <span className="absolute top-4 left-4 bg-secondary text-white text-xs font-bold px-3 py-1 tracking-wider">BEFORE</span>
      <span className="absolute top-4 right-4 bg-primary text-white text-xs font-bold px-3 py-1 tracking-wider">AFTER</span>

      <div className="absolute top-0 bottom-0 w-1 bg-white shadow-[0_0_10px_rgba(0,0,0,0.4)]" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <button
          onMouseDown={start}
          onTouchStart={start}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white shadow-lg flex items-center justify-center cursor-ew-resize"
          data-testid="slider-handle"
          aria-label="Drag to compare"
        >
          <MoveHorizontal className="h-5 w-5 text-secondary" />
        </button>
      </div>
    </div>
  );
}
